import { cn } from '@shared/lib/utils';
import { ComponentType } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Text,
  XAxis,
  XAxisProps,
  YAxis,
  YAxisProps,
} from 'recharts';
import { DataKey, Margin } from 'recharts/types/util/types';
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '../ui/chart';
import { CustomTooltipProps } from './tooltips/types';

interface DualAxisAreaChartProps<T extends string, U extends string> {
  nameKey: T;
  dataKey: U;
  leftAxisConfig: ChartConfig;
  rightAxisConfig?: ChartConfig;
  chartData: any[];
  legend?: boolean;
  className?: string;
  layout?: 'horizontal' | 'vertical';
  margin?: Margin;
  LeftYAxisProps?: YAxisProps;
  RightYAxisProps?: YAxisProps;
  XAxisProps?: XAxisProps;
  MaxBarSize?: number;
  barCategoryGap?: number | string;
  barSize?: number;
  tooltipFormatter?: ComponentType<CustomTooltipProps>;
  /** Fades each left-axis series' fill from its colour to transparent. */
  gradientFill?: boolean;
  /**
   * Solid fill opacity applied to each left-axis Area when `gradientFill`
   * is off. Defaults to Recharts' own default (~0.6) when omitted.
   */
  fillOpacity?: number;
  /** Draws a hollow marker at each right-axis (line) data point. */
  dot?: boolean;
}

/**
 * Builds a Y-axis tick renderer whose label is coloured to match its series.
 * Uses an inline `style` (not a `fill` attribute) so it beats ChartContainer's
 * `fill-muted-foreground` CSS class. Applies the axis `tickFormatter` itself,
 * since a custom tick receives the raw payload value.
 */
const coloredTick =
  (color?: string, tickFormatter?: YAxisProps['tickFormatter']) =>
  // eslint-disable-next-line react/display-name
  ({ x, y, payload, textAnchor }: any) => (
    <Text
      x={x}
      y={y}
      textAnchor={textAnchor}
      verticalAnchor="middle"
      style={{ fill: color, fontSize: 12 }}
    >
      {tickFormatter
        ? tickFormatter(payload.value, payload.index)
        : payload.value}
    </Text>
  );

export default function DualAxisAreaChart<
  T extends DataKey<string>,
  U extends DataKey<string[]>,
>({
  nameKey,
  dataKey,
  chartData,
  legend,
  className,
  margin = {
    top: 10,
    right: 10,
    bottom: 10,
    left: 10,
  },
  LeftYAxisProps,
  RightYAxisProps,
  XAxisProps,
  tooltipFormatter: Component,
  leftAxisConfig,
  rightAxisConfig,
  gradientFill = false,
  fillOpacity,
  dot = false,
}: DualAxisAreaChartProps<T, U>) {
  const allConfigs = { ...leftAxisConfig, ...rightAxisConfig };
  const chartConfig = Object.keys(allConfigs).reduce((acc, key) => {
    const config = allConfigs[key];
    acc[key] = {
      label: config.label,
      color: config.color,
      unit: config.unit,
    };
    return acc;
  }, {} as ChartConfig);

  // Colour each axis's ticks to match its series, so it's clear which axis
  // belongs to which line/area (left = emissions, right = carbon intensity).
  // A custom tick with an inline `style` is required: ChartContainer applies
  // `fill-muted-foreground` to tick text via a CSS class, which would override
  // the SVG `fill` presentation attribute a plain `tick={{ fill }}` sets.
  const leftSeriesColor = Object.values(leftAxisConfig)[0]?.color;
  const rightSeriesColor = rightAxisConfig
    ? Object.values(rightAxisConfig)[0]?.color
    : undefined;

  return (
    <ChartContainer
      config={allConfigs}
      className={(cn('aspect-auto h-full w-full'), className)}
    >
      <AreaChart data={chartData} margin={margin} className="w-full">
        <defs>
          <linearGradient id="fillDesktop" x1="0" y1="0" x2="0" y2="1">
            <stop
              offset="5%"
              stopColor="var(--color-desktop)"
              stopOpacity={0.8}
            />
            <stop
              offset="95%"
              stopColor="var(--color-desktop)"
              stopOpacity={0.1}
            />
          </linearGradient>
          <linearGradient id="fillMobile" x1="0" y1="0" x2="0" y2="1">
            <stop
              offset="5%"
              stopColor="var(--color-mobile)"
              stopOpacity={0.8}
            />
            <stop
              offset="95%"
              stopColor="var(--color-mobile)"
              stopOpacity={0.1}
            />
          </linearGradient>
          {gradientFill &&
            Object.keys(leftAxisConfig).map((key) => (
              <linearGradient
                key={key}
                id={`dual-area-fill-${key}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor={chartConfig[key].color}
                  stopOpacity={0.18}
                />
                <stop
                  offset="100%"
                  stopColor={chartConfig[key].color}
                  stopOpacity={0}
                />
              </linearGradient>
            ))}
        </defs>
        <CartesianGrid vertical={false} stroke="var(--border)" />
        <XAxis
          dataKey={dataKey}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          {...XAxisProps}
        />
        <YAxis
          yAxisId="left"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={(value) => value}
          {...LeftYAxisProps}
          tick={coloredTick(leftSeriesColor, LeftYAxisProps?.tickFormatter)}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          hide={rightAxisConfig ? false : true}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={(value) => value}
          {...RightYAxisProps}
          tick={coloredTick(rightSeriesColor, RightYAxisProps?.tickFormatter)}
        />
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              className={cn('w-60', {
                'w-fit': Component,
              })}
              indicator="dot"
              formatter={
                Component
                  ? (value, name, item, index) => (
                      <Component
                        value={value}
                        name={name}
                        item={item}
                        index={index}
                        chartConfig={chartConfig}
                      />
                    )
                  : undefined
              }
            />
          }
        />
        {legend && (
          <ChartLegend
            content={<ChartLegendContent className="text-md" />}
            verticalAlign="bottom"
          />
        )}

        {Object.keys(leftAxisConfig).map((key) => (
          <Area
            dataKey={key}
            key={key}
            stackId={key}
            fill={
              gradientFill
                ? `url(#dual-area-fill-${key})`
                : chartConfig[key].color
            }
            fillOpacity={fillOpacity}
            stroke={chartConfig[key].color}
            type="monotoneX"
            isAnimationActive={false}
            yAxisId="left"
            strokeWidth={2}
          />
        ))}

        {rightAxisConfig &&
          Object.keys(rightAxisConfig).map((key) => (
            <Area
              dataKey={key}
              key={key}
              stackId={key}
              fill="none"
              stroke={chartConfig[key].color}
              type="monotoneX"
              isAnimationActive={false}
              yAxisId="right"
              strokeWidth={2}
              dot={dot ? { r: 3, fill: '#fff', strokeWidth: 2 } : false}
            />
          ))}
      </AreaChart>
    </ChartContainer>
  );
}
